import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  inject,
  Input,
  OnInit,
  ViewChild,
  DOCUMENT
} from '@angular/core';
import { LanguageService } from 'src/app/services/language.service';
import { environment } from 'src/environments/environment';

import { HOBEX } from 'src/app/app.constants';
import { Icons } from 'src/app/icons';
import { LabelComponent } from '@progress/kendo-angular-label';
import { TranslatePipe } from '@ngx-translate/core';
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { logger } from 'src/app/utils';
import { ROUTE_PATHS } from 'src/app/app.routes';
import { query } from '@angular/animations';
import { UrlService } from 'src/app/services/url.service';

@Component({
    selector: 'app-payment-form',
    imports: [
        LabelComponent,
        TranslatePipe,
        SVGIconComponent
    ],
    templateUrl: './payment-form.component.html',
    styleUrl: './payment-form.component.scss'
})
export class PaymentFormComponent implements OnInit, AfterViewInit {
  protected readonly icons = Icons;
  protected readonly formId = HOBEX.WIDGET_FORM_ID;
  protected languageService = inject(LanguageService);
  private urlService = inject(UrlService);

  @ViewChild('scriptContainer') scriptContainer: ElementRef<HTMLDivElement>;

  @Input({required: true}) contractId: string;
  @Input({required: true}) ndc: string;
  @Input({required: true}) checkoutId: string;

  hobexFormAction: string;
  hobexFormInitializerUrl: string;

  constructor(@Inject(DOCUMENT) private document: Document) {
  }

  ngOnInit() {
    this.configHobexForm();
    const queryParams = {
      contractId: this.contractId,
      ndc: this.ndc,
      lang: this.languageService.currentLanguage()
    };
    this.hobexFormAction = this.urlService.getUrlForRoute(ROUTE_PATHS.CHECK_STATUS, queryParams);
    this.hobexFormInitializerUrl = environment.paymentWidgetUrl + '?checkoutId=' + this.checkoutId;
  }

  private configHobexForm() {

    const afterSubmit = () => {
      document.getElementById('paymentContent')?.setAttribute('style', 'display: none');
      const resultPaymentIframe = document.getElementById('resultPaymentIframe') as HTMLIFrameElement;
      resultPaymentIframe.onload = () => {
        logger.i('resultPaymentIframe loaded');
        const paymentResultContent = document.getElementById('paymentResultContent');
        if (paymentResultContent) {
          paymentResultContent.style.display = 'flex';
          paymentResultContent.style.flexDirection = 'column';
        }
      }
    }

    const styleInputsOrder = () => {
      const expiryGroup = document.querySelector('.wpwl-group-expiry');
      const cardHolderGroup = document.querySelector('.wpwl-group-cardHolder');
      if (expiryGroup && cardHolderGroup) {
        cardHolderGroup.insertAdjacentElement('afterend', expiryGroup);
      }
      const cvvGroup = document.querySelector('.wpwl-group-cvv');
      const expiryCvvGroup = document.createElement('div');
      expiryCvvGroup.style.display = 'flex';
      expiryCvvGroup.style.gap = '8px';
      if (expiryGroup && cvvGroup && cardHolderGroup) {
        expiryCvvGroup.insertAdjacentElement('beforeend', expiryGroup);
        expiryCvvGroup.insertAdjacentElement('beforeend', cvvGroup);
        cardHolderGroup.insertAdjacentElement('afterend', expiryCvvGroup);
      }
    }

    const styleCardType = () => {
      const groupBrand = document.querySelector('.wpwl-group-brand');
      const labelBrand = document.querySelector('.wpwl-label-brand');
      const wrapperBrand = document.querySelector('.wpwl-wrapper-brand');
      const brand = document.querySelector('.wpwl-brand');
      if (groupBrand && labelBrand && wrapperBrand && brand) {
        const brandContainer = document.createElement('div');
        brandContainer.className = 'wpwl-cardtype';
        brandContainer.insertAdjacentElement('beforeend', wrapperBrand);
        brandContainer.insertAdjacentElement('beforeend', brand);
        groupBrand.insertAdjacentElement('beforeend', brandContainer);
      }
    }

    const styleInputs = () => {
      const labels = document.querySelectorAll('.wpwl-label');
      labels.forEach(label => label.setAttribute('style', 'color: white'));
      const inputs = document.querySelectorAll('.wpwl-wrapper input');
      inputs.forEach(i => {
        i.classList.add('k-input', 'k-input-lg', 'k-rounded-md', 'k-input-solid', 'k-input-inner');
        i.setAttribute('style', 'font-size: var(--kendo-font-size-lg)');
      });
    }

    const stylePayButton = () => {
      const button = document.querySelector('.wpwl-button-pay');
      const buttonParent = button?.parentElement;
      if (button && buttonParent) {
        buttonParent.style.display = 'flex';
        buttonParent.style.width = '100%';
        button.classList.add(
          'k-button', 'k-button-lg', 'k-rounded-md',
          'k-button-solid-primary', 'k-button-solid'
        );
      }
    }

    const styleSSLLabel = () => {
      const sslLabel = document.getElementById('sslLabel');
      if (sslLabel) sslLabel.style.display = 'flex';
    }

    (window as any).wpwlOptions = {
      locale: this.languageService.currentLanguage(),
      style: 'plain',
      spinner: {
        color: 'white'
      },
      threeDIframeSize: {
        width: '100%',
        height: '100%'
      },
      showCVVHint: false,
      brandDetection: false,
      disableSubmitOnEnter: true,
      paymentTarget: 'iframe_a',
      shopperResultTarget: 'iframe_a',
    /*  applePay: {
        displayName: "Papin Sport",
        total: {label: "Papin Sport GmbH"},
        buttonStyle: "white",
        supportedNetworks: ["masterCard", "visa"],
        onPaymentAuthorized: function (e: any) {
          logger.i(e);
          afterSubmit();
        }
      },*/
      onError: function (e: any) {
        logger.i('HOBEX ERROR', e);
        window.parent.postMessage({type: 'hobexError', error: e});
      },
      onReady: function (e: any) {
        logger.i('onReady', e);
        setTimeout(() => {
          styleSSLLabel();
          styleInputsOrder();
          styleCardType();
          styleInputs();
          stylePayButton();
        }, 300);
      },
      onAfterSubmit: function () {
        logger.i('afterSubmit');
        afterSubmit();
      },
    };
  }

  ngAfterViewInit() {
    const paymentFormScript = this.document.createElement('script');
    paymentFormScript.async = true;
    paymentFormScript.type = 'text/javascript';
    paymentFormScript.src = this.hobexFormInitializerUrl;
    this.scriptContainer.nativeElement.appendChild(paymentFormScript);
  }

}
